// PaymentFailure: Failure page for failed Stripe payments
// Provides helpful recovery options and support information

import React from 'react'

interface PaymentFailureProps {
  onRetry?: () => void
  onViewPricing?: () => void
  className?: string
  errorMessage?: string
}

export function PaymentFailure({ 
  onRetry, 
  onViewPricing, 
  className = '',
  errorMessage 
}: PaymentFailureProps) {

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    } else {
      // Default: redirect to pricing
      window.location.href = '/#pricing'
    }
  }

  const handleViewPricing = () => {
    if (onViewPricing) {
      onViewPricing()
    } else {
      // Default: redirect to pricing
      window.location.href = '/#pricing'
    }
  }

  return (
    <div className={`max-w-2xl mx-auto text-center py-12 ${className}`}>
      <div className="bg-white rounded-2xl shadow-lg border p-8 space-y-6">
        {/* Error Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-red-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          </div>
        </div>

        {/* Error Message */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">
            Payment Failed
          </h1>
          <p className="text-lg text-gray-600">
            We couldn't process your payment
          </p>
          {errorMessage && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              {errorMessage}
            </p>
          )}
        </div>

        {/* Common Reasons */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-left">
          <h3 className="font-semibold text-yellow-900 mb-3">Common Reasons for Payment Failure:</h3>
          <ul className="space-y-2 text-sm text-yellow-800">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Insufficient funds in your account
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Card expired or incorrect card information
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Bank declined the transaction
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              Payment method doesn't support international transactions
            </li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={handleRetry}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleViewPricing}
            className="flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            View Pricing
          </button>
        </div>

        {/* Alternative Options */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left">
          <h3 className="font-semibold text-blue-900 mb-3">Need Help?</h3>
          <div className="space-y-3 text-sm text-blue-800">
            <div>
              <strong>Try a different payment method:</strong>
              <br />
              We accept all major credit cards, debit cards, and digital wallets.
            </div>
            <div>
              <strong>Contact your bank:</strong>
              <br />
              Some banks block international or online transactions by default.
            </div>
            <div>
              <strong>Use our free trial:</strong>
              <br />
              Start with our free credits while you resolve payment issues.
            </div>
          </div>
        </div>

        {/* Back to Upload */}
        <div className="pt-4 border-t">
          <button
            onClick={() => window.location.href = '/#upload'}
            className="text-gray-600 hover:text-gray-800 text-sm underline"
          >
            ← Continue with free trial
          </button>
        </div>

        {/* Support Note */}
        <div className="text-xs text-gray-500 pt-4 border-t">
          <p>
            Still having trouble? Contact our support team at{' '}
            <a 
              href="mailto:support@stocktagger.ai" 
              className="text-blue-600 hover:text-blue-800"
            >
              support@stocktagger.ai
            </a>
            {' '}and we'll help you get set up.
          </p>
        </div>
      </div>
    </div>
  )
}

export default PaymentFailure